import dayjs, { type Dayjs } from "dayjs"
import { applyMiddleware, combineReducers, createStore, type AnyAction, type Reducer } from "redux"
import { persistReducer, persistStore } from "redux-persist"
import createWebStorage from "redux-persist/es/storage/createWebStorage"
import thunk, { type ThunkDispatch } from "redux-thunk"

import type { Cohort, Configuration, OtpIdentifier, Participant, Signup } from "@/types/api-types"
import type { GroupedShifts, PreparedShift } from "@/hooks/use_fetch_available_shifts"
import type { Insight } from "@/types/self_recording_api/insight"
import type { Conversation } from "@/types/self_recording_api/conversation"
import { AUTH } from "../actions/auth"
import auth from "./reducer_auth"

export type LoggedInAuth = { user: Participant; token: string }
export type AuthState = LoggedInAuth | null

export type RootStateSlices = {
  inAction: boolean
  signupCohort: Cohort | null
  didAnswerAntibioticsQuestion: boolean | null
  minimumVisitDate: Dayjs | null
  appointmentsSchedulingAvailableShifts: GroupedShifts[] | null
  appointmentsScheduledShift: PreparedShift | null
  appointmentsSchedluingSelectedDate: Dayjs | null
  appointmentsSchedluingSelectedShift: PreparedShift | null
  shiftCalendarCurrentMonth: number
  shiftCalendarCurrentYear: number
  currentLanguageCode: string | null
  configuration: Configuration | null
  authPayload: { identifier: OtpIdentifier; signupPayload: Signup | null } | null
  disclaimerAccepted: boolean
  didSubmitAntibioticsDate: boolean
  currentInsight: Insight | null
  currentConversation: Conversation | null
  conversationWaitForResponse: boolean
  conversationsHistory: Conversation[] | null
  insights: Insight[] | null
  insightsConsentApproved: boolean
  loginAt: number
  sessionHeartbeatAt: number
}

type SliceAction = { type: string; payload?: unknown }

// Factory for the dynamic slice reducers. Each slice listens for a single
// action type equal to its key and replaces its state with `action.payload`
// (or leaves it untouched when payload is undefined). Callers dispatch via
// useReduxState, which wires the key/payload contract on the dispatch side.
const createReducer = <S>(key: string, initialState: S): Reducer<S, SliceAction> => {
  return (state: S | undefined = initialState, action: SliceAction): S => {
    if (action.type === key) {
      return action.payload !== undefined ? (action.payload as S) : state
    }
    return state
  }
}

const slices: { [K in keyof RootStateSlices]: Reducer<RootStateSlices[K], SliceAction> } = {
  inAction: createReducer<boolean>("inAction", false),
  signupCohort: createReducer<Cohort | null>("signupCohort", null),
  didAnswerAntibioticsQuestion: createReducer<boolean | null>("didAnswerAntibioticsQuestion", null),
  minimumVisitDate: createReducer<Dayjs | null>("minimumVisitDate", null),
  appointmentsSchedulingAvailableShifts: createReducer<GroupedShifts[] | null>(
    "appointmentsSchedulingAvailableShifts",
    null,
  ),
  appointmentsScheduledShift: createReducer<PreparedShift | null>("appointmentsScheduledShift", null),
  appointmentsSchedluingSelectedDate: createReducer<Dayjs | null>("appointmentsSchedluingSelectedDate", null),
  appointmentsSchedluingSelectedShift: createReducer<PreparedShift | null>("appointmentsSchedluingSelectedShift", null),
  shiftCalendarCurrentMonth: createReducer<number>("shiftCalendarCurrentMonth", dayjs().month()),
  shiftCalendarCurrentYear: createReducer<number>("shiftCalendarCurrentYear", dayjs().year()),
  currentLanguageCode: createReducer<string | null>("currentLanguageCode", null),
  configuration: createReducer<Configuration | null>("configuration", null),
  authPayload: createReducer<RootStateSlices["authPayload"]>("authPayload", null),
  disclaimerAccepted: createReducer<boolean>("disclaimerAccepted", false),
  didSubmitAntibioticsDate: createReducer<boolean>("didSubmitAntibioticsDate", false),
  currentInsight: createReducer<Insight | null>("currentInsight", null),
  currentConversation: createReducer<Conversation | null>("currentConversation", null),
  conversationWaitForResponse: createReducer<boolean>("conversationWaitForResponse", false),
  conversationsHistory: createReducer<Conversation[] | null>("conversationsHistory", null),
  insights: createReducer<Insight[] | null>("insights", null),
  insightsConsentApproved: createReducer<boolean>("insightsConsentApproved", false),
  loginAt: createReducer<number>("loginAt", 0),
  sessionHeartbeatAt: createReducer<number>("sessionHeartbeatAt", 0),
}

const appReducer = combineReducers({ auth, ...slices })

export type RootState = ReturnType<typeof appReducer>

// Discriminated views of RootState. Both rely on DataLoader having gated
// rendering on `configuration` being loaded, and on the route HOC having
// gated on `auth`. Use useLoggedInAppSelector / useLoggedOutAppSelector.
export type LoggedInState = Omit<RootState, "auth" | "configuration"> & {
  auth: LoggedInAuth
  configuration: Configuration
}
export type LoggedOutState = Omit<RootState, "auth" | "configuration"> & {
  auth: null
  configuration: Configuration
}

const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === AUTH && !action.payload) {
    // On logout, drop every slice except the ones we explicitly carry over.
    // combineReducers will fill the rest with their initial values via the
    // child reducers. The cast is needed because the literal here is a
    // deliberate partial that combineReducers immediately completes.
    state = {
      currentLanguageCode: state?.currentLanguageCode,
    } as RootState
  }
  return appReducer(state, action)
}

type SsrStorageStub = {
  getItem: (key: string) => Promise<string | null>
  setItem: (key: string, value: string) => Promise<void>
  removeItem: (key: string) => Promise<void>
}

export function createPersistStore(): SsrStorageStub | ReturnType<typeof createWebStorage> {
  if (typeof window === "undefined") {
    return {
      getItem() {
        return Promise.resolve(null)
      },
      setItem() {
        return Promise.resolve()
      },
      removeItem() {
        return Promise.resolve()
      },
    }
  }
  return createWebStorage("local")
}

const storage = typeof window !== "undefined" ? createWebStorage("local") : createPersistStore()

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "auth",
    "currentLanguageCode",
    "authPayload",
    "disclaimerAccepted",
    "insightsConsentApproved",
    "loginAt",
    "sessionHeartbeatAt",
  ],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = createStore(persistedReducer, applyMiddleware(thunk))
export const persistor = persistStore(store)

if (typeof localStorage !== "undefined" && localStorage.length === 0) {
  void persistor.purge()
}

// createStore() in redux@5 doesn't widen dispatch with the middleware's
// ThunkDispatch overload, so we declare AppDispatch as the intersection. This
// matches what redux-thunk actually accepts at runtime: plain actions and
// thunk functions alike.
export type AppDispatch = typeof store.dispatch & ThunkDispatch<RootState, undefined, AnyAction>
export type AppStore = typeof store

export const appDispatch: AppDispatch = store.dispatch as AppDispatch
